import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { getPriceHistory, getRooms } from "../lib/api";
import { toast } from "sonner";
import { History, TrendingUp, TrendingDown, Minus, Calendar } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const PriceHistory = () => {
  const [history, setHistory] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [historyRes, roomsRes] = await Promise.all([
        getPriceHistory(selectedRoom === "all" ? null : selectedRoom),
        getRooms(),
      ]);
      setHistory(historyRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedRoom]);

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.name || "Habitación desconocida";
  };

  const getPriceChange = (oldPrice, newPrice) => {
    const diff = newPrice - oldPrice;
    const percent = oldPrice > 0 ? ((diff / oldPrice) * 100).toFixed(1) : 0;
    
    if (diff > 0) {
      return {
        icon: TrendingUp,
        text: `+€${diff.toFixed(2)} (+${percent}%)`,
        className: "text-emerald-600 dark:text-emerald-400",
        badge: "badge-success"
      };
    } else if (diff < 0) {
      return {
        icon: TrendingDown,
        text: `€${diff.toFixed(2)} (${percent}%)`,
        className: "text-red-600 dark:text-red-400",
        badge: "badge-danger"
      };
    }
    return {
      icon: Minus,
      text: "Sin cambio",
      className: "text-muted-foreground",
      badge: ""
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="price-history-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Historial de Precios</h1>
          <p className="text-muted-foreground">Seguimiento de todos los cambios de precio</p>
        </div>
        <Select value={selectedRoom} onValueChange={setSelectedRoom}>
          <SelectTrigger className="w-[200px]" data-testid="room-filter-select">
            <SelectValue placeholder="Filtrar por habitación" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las habitaciones</SelectItem>
            {rooms.map((room) => (
              <SelectItem key={room.id} value={room.id}>
                {room.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {history.length === 0 ? (
        <Card className="p-12 text-center">
          <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Sin historial de cambios</h3>
          <p className="text-muted-foreground">
            Los cambios de precio aparecerán aquí cuando el motor de precios realice ajustes automáticos.
          </p>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Cambios Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha y Hora</TableHead>
                    <TableHead>Habitación</TableHead>
                    <TableHead className="text-right">Precio Anterior</TableHead>
                    <TableHead className="text-right">Precio Nuevo</TableHead>
                    <TableHead className="text-center">Cambio</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((entry) => {
                    const change = getPriceChange(entry.old_price, entry.new_price);
                    const ChangeIcon = change.icon;
                    
                    return (
                      <TableRow key={entry.id} data-testid={`history-row-${entry.id}`}>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(entry.timestamp), "dd MMM yyyy, HH:mm", { locale: es })}
                        </TableCell>
                        <TableCell className="font-medium">{getRoomName(entry.room_type_id)}</TableCell>
                        <TableCell className="text-right font-mono">€{entry.old_price.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">€{entry.new_price.toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          <div className={`flex items-center justify-center gap-1 ${change.className}`}>
                            <ChangeIcon className="w-4 h-4" />
                            <span className="text-sm">{change.text}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal">
                            {entry.reason}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {history.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold">{history.length}</p>
                <p className="text-sm text-muted-foreground">Total de cambios</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                  {history.filter(h => h.new_price > h.old_price).length}
                </p>
                <p className="text-sm text-muted-foreground">Aumentos de precio</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  {history.filter(h => h.new_price < h.old_price).length}
                </p>
                <p className="text-sm text-muted-foreground">Reducciones de precio</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PriceHistory;
